import { IndexRoute, Router, Redirect } from 'react-router'
import { Route } from 'react-router-dom'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { applyMiddleware, createStore, Store } from 'redux'
// @ts-ignore
import thunk from 'redux-thunk'
import { Provider } from 'react-redux'
import { values } from 'lodash'

import { withStaticProps } from 'lib/decorators'
import * as blUrls from 'lib/urls'
import { getHistory } from 'lib/history'
import * as BLConstants from 'shared/constants'

import PostTeaserList from 'hello_baby/components/PostTeaserList/PostTeaserList'
import TopicView from '../components/TopicView/TopicView'
import { withContextualizedTracking, track } from 'lib/analytics'
import {
  setSearchId,
  GUIDE_SEARCH_INDEX_KEY,
} from 'shared/hooks/useSearchId/useSearchId'
import { getTopicSlug } from '../lib'
import helloBabyReducer, { getPostBySlug, getTopics } from '../reducers'
import App from '../components/App'
import LandingPage from '../components/LandingPage/LandingPage'
import Post from './Post'
import PostsDataLoader from './posts-data-loader'
import PostListView from '../components/post-list-view/post-list-view'
import AdminTool from '../components/admin-tool'
import { FlagsProvider } from 'flagged'
import { useEffect, useState } from 'react'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import { CompatRouter } from 'react-router-dom-v5-compat'

const middlewares = [thunk]

if (process.env.NODE_ENV === 'development') {
  // middlewares.push(createLogger())
}

const createStoreWithMiddleware = applyMiddleware(...middlewares)(createStore)

const scrollTo = (elId: string) => {
  const el = document.getElementById(elId)
  if (el) {
    document.body.scrollTop = el.offsetTop - 80 // 80px to account for sticky nav
  }
}

interface GuidesAppProps {
  collections: {
    'hello-baby-home-topics': object
    topicNavigation: object
  }
  emailUrls: [string]
  featureFlags: {}
  isEmailSubscriber: boolean
  minimalView: boolean
  path: string
  previewPost?: Post
  posts?: object
  rootPath: string
  searchId?: string
  searchPosts: []
  sectionId: string
}

const GuidesApp = ({
  collections,
  emailUrls,
  featureFlags,
  isEmailSubscriber,
  minimalView,
  path,
  posts,
  previewPost,
  rootPath,
  searchId,
  searchPosts,
  sectionId,
}: GuidesAppProps) => {
  const [currentUser] = useCurrentUser()

  const [store] = useState<Store<unknown>>(
    createStoreWithMiddleware(helloBabyReducer, {
      collections,
      posts,
    })
  )

  const history = getHistory({ path, useRouter: true })

  const topicNavigation =
    collections[BLConstants.COLLECTION_SLUG_TOPIC_NAVIGATION]

  const homeTopics = collections[BLConstants.COLLECTION_SLUG_HOME_TOPICS]

  const DecoratedLandingPage = withStaticProps('LandingPage', {
    isEmailSubscriber,
    topicNavigation,
    homeTopics,
  })(LandingPage)

  const DecoratedApp = withStaticProps('App', {
    path,
    minimalView,
    topicNavigation,
  })(App)

  const DecoratedPost = withStaticProps('Post', {
    emailUrls,
    minimalView,
    previewPost,
  })(Post)

  useEffect(() => {
    const slug = path.split('/').pop() as string

    updateGAMetrics(slug, path)

    scrollToArticleInPathVariable()

    if (searchId) {
      setSearchId(GUIDE_SEARCH_INDEX_KEY, searchId)
    }
  }, [])

  const scrollToArticleInPathVariable = () => {
    if (sectionId) {
      scrollTo(sectionId)
    }
  }

  const updateGAMetrics = (slug: string, pathname: string) => {
    if (typeof window === 'undefined') return

    const { ga } = window

    if (!ga) return

    let type = null
    let publishedAtYYYYMM = null

    if (slug) {
      const post = getPostBySlug(slug, posts)
      if (post && post.publishedAt) {
        publishedAtYYYYMM = post.publishedAt.slice(0, 7)
        type = getTopicSlug(post) || post.type
      }
    } else {
      type = pathname.split('/').pop()
    }

    // Content groups must be set before the pageview event is sent.
    // The values persist across router navigation unless explicity 'unset' by setting to null
    ga('set', 'contentGroup1', type) // GA content grouping 'Hello Baby Topics'
    ga('set', 'contentGroup2', publishedAtYYYYMM) // GA content grouping 'Hello Baby Publish Date'
    ga('set', 'contentGroup3', 'Hello Baby') // GA content grouping 'Content Group'
    ga('send', 'pageview', pathname) // send pageview event
  }

  const searchResults = values(searchPosts)

  const isAdmin = currentUser?.admin

  const postRedirects = [
    {
      from: `${blUrls.helloBabyPath}/chosing-your-nuna-infant-car-seat`,
      to: `${blUrls.helloBabyPath}/nuna-infant-car-seat-comparison`,
    },
    {
      from: `${blUrls.helloBabyPath}/pottery-barn-kids-baby-holiday-gifts`,
      to: `${blUrls.helloBabyPath}/best-gifts-baby-first-holiday`,
    },
    {
      from: `${blUrls.helloBabyPath}/nursery-design-tips`,
      to: `${blUrls.helloBabyPath}/nursery-organization-hacks`,
    },
    {
      from: `${blUrls.helloBabyPath}/video-the-ergonomic-babybjoern-bouncer-with-thousands-of-5-star-reviews`,
      to: `${blUrls.helloBabyPath}/baby-bjorn-bouncer-balance-soft-video`,
    },
    {
      from: `${blUrls.helloBabyPath}/video-the-ergonomic-babybjorn-bouncer-with-thousands-of-5-star-reviews`,
      to: `${blUrls.helloBabyPath}/baby-bjorn-bouncer-balance-soft-video`,
    },
    {
      from: `${blUrls.helloBabyPath}/anti-racist-starter-kit-babies-toddlers`,
      to: `${blUrls.helloBabyPath}/talking-about-race-toddler`,
    },
  ]

  const topics = getTopics(store.getState())

  return (
    <Provider store={store}>
      <GoogleReCaptchaProvider reCaptchaKey={BLConstants.RECAPTCHA_V3_SITE_KEY}>
        <FlagsProvider features={featureFlags}>
          {isAdmin && <AdminTool />}
          <Router history={history}>
            <CompatRouter>
              <Route component={DecoratedApp} path={rootPath}>
                <IndexRoute component={DecoratedLandingPage} />
                <Route
                  component={(routerProps: any) => (
                    <PostsDataLoader shouldFetchPosts>
                      <PostListView
                        {...routerProps}
                        description={`${searchResults.length} results`}
                        emailUrls={emailUrls}
                        title={`'${routerProps.location.query.search_term}'`}
                        topicNavigation={topicNavigation}
                        searchResults={searchResults}
                      >
                        <PostTeaserList posts={searchResults} />
                      </PostListView>
                    </PostsDataLoader>
                  )}
                  path={blUrls.helloBabyPostPath('search')}
                />
                <Route
                  component={(routerProps: any) => (
                    <PostListView
                      {...routerProps}
                      emailUrls={emailUrls}
                      topicNavigation={topicNavigation}
                    />
                  )}
                  path="/"
                >
                  {Object.values(topics).map((topic) => (
                    <Route
                      component={() => (
                        <TopicView emailUrls={emailUrls} slug={topic.slug} />
                      )}
                      key={topic.slug}
                      path={blUrls.helloBabyPostPath(topic.slug)}
                    />
                  ))}
                </Route>
                {postRedirects.map((redirect) => (
                  <Redirect
                    from={redirect.from}
                    key={redirect.from}
                    to={redirect.to}
                  />
                ))}
                <Route component={DecoratedPost} path={`${rootPath}/:slug`} />
                {/* used by iOS app for feed guides */}
                <Route
                  component={DecoratedPost}
                  path={`${rootPath}/embedded/:slug`}
                />
              </Route>
            </CompatRouter>
          </Router>
        </FlagsProvider>
      </GoogleReCaptchaProvider>
    </Provider>
  )
}

export default withContextualizedTracking({
  eventLocation: track.EventLocation.GUIDE,
})(GuidesApp)
